import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Robot, PaperPlaneRight, X, Sparkle, Warning, House } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';
import useIA from '../hooks/useIA';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B6 (P1#28) — quand l'assistant ne peut pas répondre, il propose la vue d'ensemble du
// chantier (la reprise d'un dossier ne dépend pas de l'assistant) au lieu d'un message d'erreur.
const MESSAGE_INDISPONIBLE = "Je ne peux pas répondre pour le moment. En attendant, la vue d'ensemble du chantier résume le dossier : étape, prochaine réunion, dernières actions et documents.";

export default function AIChat({ chantierId, chantierName, onClose, onVoirSynthese }) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: `Bonjour ! Je suis l'assistant IA du chantier "${chantierName}".\n\nJe peux vous aider à :\n- Rechercher des informations dans le dossier\n- Répondre à vos questions sur l'avancement\n- Expliquer les prochaines étapes\n\nComment puis-je vous aider ?`,
      timestamp: new Date().toISOString(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  // RP96 — réglage IA du cabinet : saisie grisée avec explication si non disponible
  const ia = useIA();
  const iaOff = !ia.disponible('assistant');

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = { id: Date.now(), role: 'user', content: input.trim(), timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const historique = messages
        .filter((m) => m.role !== 'system')
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const { data } = await axios.post(
        `${API_URL}/api/ai/chat`,
        { chantier_id: chantierId, message: userMessage.content, historique_session: historique },
        { withCredentials: true }
      );

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.response,
          source: data.source,
          requires_validation: data.requires_validation,
          redirige_architecte: data.redirige_architecte,
          indisponible: !!data.indisponible,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: MESSAGE_INDISPONIBLE, indisponible: true, timestamp: new Date().toISOString() },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white" data-testid="ai-chat-panel">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#6366F1] flex items-center justify-center">
            <Robot size={22} weight="bold" className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Assistant IA</h3>
            <p className="text-xs text-slate-500">{chantierName}</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-ai-chat">
          <X size={20} />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-4 ai-chat-bg" ref={scrollRef}>
        <div className="space-y-4">
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] ${
                    message.role === 'user'
                      ? 'bg-[#0047FF] text-white p-4'
                      : message.isError
                      ? 'bg-red-50 border border-red-200 text-red-700 p-4'
                      : 'bg-white border border-slate-200 p-4'
                  }`}
                >
                  {message.role === 'assistant' && !message.isError && (
                    <div className="flex items-center gap-2 mb-2 text-xs text-[#6366F1] font-medium">
                      <Sparkle size={12} weight="fill" />
                      Assistant IA
                    </div>
                  )}
                  <div className={`text-sm whitespace-pre-wrap ${message.role === 'assistant' && !message.isError ? 'text-slate-700' : ''}`}>
                    {message.content}
                  </div>
                  {/* RP05 — plus de promesse fausse : aucune notification n'est envoyée
                      et aucun circuit de validation n'existe. On dit ce qui est vrai. */}
                  {message.indisponible && onVoirSynthese && (
                    <button type="button" onClick={onVoirSynthese}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-xs font-medium bg-[#1E3A5F] text-white hover:bg-[#2C4F7A]"
                      data-testid="ai-chat-voir-synthese">
                      <House size={14} /> Voir la vue d'ensemble du chantier
                    </button>
                  )}
                  {message.redirige_architecte && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-xs text-amber-700 flex items-center gap-1 bg-amber-50 px-2 py-1" data-testid="ai-chat-sensitive-note">
                        <Warning size={14} weight="fill" />
                        Sujet sensible : cette réponse automatique n'engage pas le cabinet. Posez la question directement à l'architecte.
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {isLoading && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
              <div className="bg-white border border-slate-200 p-4 border-l-2 border-l-[#6366F1]">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#6366F1] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-[#6366F1] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-[#6366F1] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span className="text-xs text-slate-500">Analyse en cours...</span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      <div className="p-4 border-t border-slate-200 bg-white">
        {iaOff && (
          <p className="text-xs text-slate-600 bg-slate-100 px-3 py-2 mb-2" data-testid="ai-chat-off">{ia.raison('assistant')}</p>
        )}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={iaOff}
            placeholder="Posez votre question sur ce chantier..."
            className="flex-1 resize-none rounded-none border-slate-300 min-h-[44px] max-h-32"
            rows={1}
            data-testid="ai-chat-input"
          />
          <Button type="submit" disabled={!input.trim() || isLoading || iaOff} title={ia.raison('assistant')} className="bg-[#6366F1] hover:bg-[#4F46E5] rounded-none px-4" data-testid="ai-chat-submit">
            <PaperPlaneRight size={18} weight="bold" />
          </Button>
        </form>
        <p className="text-xs text-slate-400 mt-2 text-center">Entrée pour envoyer, Shift+Entrée pour un saut de ligne</p>
      </div>
    </div>
  );
}
